import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EmploymentType, UserRole } from '../types';
import { usePermissions, FEATURES } from '../hooks/usePermissions';
import type { FeatureKey, PermissionAction } from '../lib/permissions';
import {
  Home,
  FileText,
  MessageSquare,
  DollarSign,
  Users,
  UserCheck,
  Truck,
  ShieldCheck,
  FolderOpen,
  FolderPlus,
  Building2,
  Briefcase,
  Package,
  UserCog,
  Target,
  TrendingUp,
  UserPlus,
  ClipboardList,
  ListChecks,
  KanbanSquare,
  Users2,
  ShoppingCart,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Network,
  BadgeCheck,
  CalendarDays,
  MapPin,
  ClipboardCheck,
  IdCard,
  Headphones,
  Settings,
  Bell,
} from 'lucide-react';

interface SidebarProps {
  userRole: UserRole;
  employmentType?: EmploymentType | null;
  activeItem: string;
  onNavigate: (item: string) => void;
}

interface LeafItem {
  kind: 'leaf';
  id: string;
  label: string;
  icon: React.ReactNode;
  /** Funcionalidade cuja permissão `view` libera o item. */
  feature: FeatureKey;
  /**
   * Alternativas: o item aparece se **qualquer** uma casar, na ação
   * informada. Usado pela fila de aprovações do Recrutamento, que serve
   * os cinco passos da cadeia — quem aprova só pelo Financeiro não tem
   * `recrutamento.requisicoes` e ainda assim precisa do item.
   */
  anyFeatures?: { features: FeatureKey[]; action?: PermissionAction };
  path: string;
  /** Restringe o item a colaboradores de um regime específico. */
  employmentTypes?: EmploymentType[];
}

interface GroupItem {
  kind: 'group';
  id: string;
  label: string;
  icon: React.ReactNode;
  children: LeafItem[];
}

type MenuEntry = LeafItem | GroupItem;

export const Sidebar: React.FC<SidebarProps> = ({
  userRole,
  employmentType,
  activeItem,
  onNavigate,
}) => {
  const { can } = usePermissions();

  const menuEntries: MenuEntry[] = [
    {
      kind: 'leaf',
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Home className="w-5 h-5" />,
      feature: FEATURES.dashboardPrincipal,
      path: '/dashboard',
    },
    {
      kind: 'leaf',
      id: 'notas-fiscais',
      label: 'Notas Fiscais',
      icon: <FileText className="w-5 h-5" />,
      feature: FEATURES.notasFiscaisTela,
      path: '/notas-fiscais',
      // Colaboradores CLT não emitem nota fiscal.
      employmentTypes: ['pj'],
    },
    {
      kind: 'leaf',
      id: 'notas-fiscais-gestao',
      label: 'Gestão de NFs',
      icon: <ClipboardCheck className="w-5 h-5" />,
      feature: FEATURES.notasFiscaisLiberacao,
      path: '/notas-fiscais/gestao',
    },
    {
      kind: 'leaf',
      id: 'financeiro',
      label: 'Financeiro',
      icon: <DollarSign className="w-5 h-5" />,
      feature: FEATURES.dashboardFinanceiro,
      path: '/financeiro',
    },
    {
      kind: 'leaf',
      id: 'feedback',
      label: 'Feedback',
      icon: <MessageSquare className="w-5 h-5" />,
      feature: FEATURES.feedbacksAvaliar,
      path: '/feedback',
    },
    {
      kind: 'leaf',
      id: 'meus-feedbacks',
      label: 'Meus Feedbacks',
      icon: <MessageSquare className="w-5 h-5" />,
      feature: FEATURES.feedbacksMeus,
      path: '/meus-feedbacks',
    },
    {
      kind: 'leaf',
      id: 'meu-gestor',
      label: 'Meu Gestor',
      icon: <UserCheck className="w-5 h-5" />,
      feature: FEATURES.meuGestor,
      path: '/meu-gestor',
    },
    {
      kind: 'leaf',
      id: 'meus-documentos',
      label: 'Meus Documentos',
      icon: <FolderOpen className="w-5 h-5" />,
      feature: FEATURES.meusDocumentos,
      path: '/meus-documentos',
    },
    {
      kind: 'leaf',
      id: 'meus-dados',
      label: 'Meus Dados',
      icon: <IdCard className="w-5 h-5" />,
      feature: FEATURES.meusDocumentos,
      path: '/meus-dados',
    },
    {
      kind: 'leaf',
      id: 'minhas-ferias',
      label: 'Minhas Férias',
      icon: <CalendarDays className="w-5 h-5" />,
      feature: FEATURES.meusDocumentos,
      path: '/minhas-ferias',
      // Férias só se aplicam a vínculo CLT.
      employmentTypes: ['clt'],
    },
    {
      kind: 'leaf',
      id: 'meus-colaboradores',
      label: 'Meus Colaboradores',
      icon: <Users className="w-5 h-5" />,
      feature: FEATURES.meusColaboradores,
      path: '/meus-colaboradores',
    },
    {
      kind: 'group',
      id: 'cau',
      label: 'Atendimento',
      icon: <Headphones className="w-5 h-5" />,
      children: [
        {
          kind: 'leaf',
          id: 'cau-catalogo',
          label: 'Catálogo de serviços',
          icon: <Headphones className="w-4 h-4" />,
          feature: FEATURES.cauCatalogo,
          path: '/cau',
        },
        {
          kind: 'leaf',
          id: 'cau-configuracoes',
          label: 'Configurações',
          icon: <Settings className="w-4 h-4" />,
          feature: FEATURES.cauConfiguracoes,
          path: '/cau/configuracoes',
        },
        {
          kind: 'leaf',
          id: 'cau-chamados',
          label: 'Meus chamados',
          icon: <ClipboardList className="w-4 h-4" />,
          feature: FEATURES.cauChamados,
          path: '/cau/chamados',
        },
        {
          kind: 'leaf',
          id: 'cau-atendimento',
          label: 'Fila de atendimento',
          icon: <ListChecks className="w-4 h-4" />,
          feature: FEATURES.cauAtendimento,
          path: '/cau/atendimento',
        },
        {
          kind: 'leaf',
          id: 'cau-aprovacoes',
          label: 'Aprovações',
          icon: <ShieldCheck className="w-4 h-4" />,
          feature: FEATURES.cauAprovacoes,
          path: '/cau/aprovacoes',
        },
        {
          kind: 'leaf',
          id: 'cau-acessos',
          label: 'Acessos',
          icon: <UserCheck className="w-4 h-4" />,
          feature: FEATURES.cauAcessos,
          path: '/cau/acessos',
        },
        {
          kind: 'leaf',
          id: 'cau-ativos',
          label: 'Meus ativos',
          icon: <Package className="w-4 h-4" />,
          feature: FEATURES.cauAtivos,
          path: '/cau/ativos',
        },
        {
          kind: 'leaf',
          id: 'cau-notificacoes',
          label: 'Notificações',
          icon: <Bell className="w-4 h-4" />,
          feature: FEATURES.cauChamados,
          path: '/cau/notificacoes',
        },
        {
          kind: 'leaf',
          id: 'cau-auditoria',
          label: 'Auditoria',
          icon: <ShieldCheck className="w-4 h-4" />,
          feature: FEATURES.cauAuditoria,
          path: '/cau/auditoria',
        },
        {
          kind: 'leaf',
          id: 'cau-dashboard',
          label: 'Indicadores',
          icon: <BarChart3 className="w-4 h-4" />,
          feature: FEATURES.cauDashboard,
          path: '/cau/dashboard',
        },
        {
          kind: 'leaf',
          id: 'cau-relatorios',
          label: 'Relatórios',
          icon: <FileText className="w-4 h-4" />,
          feature: FEATURES.cauRelatorios,
          path: '/cau/relatorios',
        },
      ],
    },
    {
      kind: 'group',
      id: 'compras',
      label: 'Compras',
      icon: <ShoppingCart className="w-5 h-5" />,
      children: [
        {
          kind: 'leaf',
          id: 'compras-solicitacoes',
          label: 'Solicitações',
          icon: <ClipboardList className="w-4 h-4" />,
          feature: FEATURES.comprasRequisicoes,
          path: '/compras',
        },
        {
          kind: 'leaf',
          id: 'compras-aprovacoes',
          label: 'Aprovações',
          icon: <ListChecks className="w-4 h-4" />,
          feature: FEATURES.comprasAprovacaoGestor,
          anyFeatures: {
            features: [
              FEATURES.comprasAprovacaoGestor,
              FEATURES.comprasAprovacaoFinanceiro,
              FEATURES.comprasAprovacaoGovernanca,
              FEATURES.comprasAprovacaoDiretoria,
            ],
            action: 'edit',
          },
          path: '/compras/aprovacoes',
        },
        {
          kind: 'leaf',
          id: 'compras-relatorios',
          label: 'Relatórios',
          icon: <BarChart3 className="w-4 h-4" />,
          feature: FEATURES.comprasRelatorios,
          path: '/compras/relatorios',
        },
        {
          kind: 'leaf',
          id: 'compras-ativos',
          label: 'Ativos',
          icon: <Package className="w-4 h-4" />,
          feature: FEATURES.ativos,
          path: '/ativos',
        },
      ],
    },
    // Grupo "Cadastros" — reúne todos os master data do sistema.
    // Novos cadastros entram como children aqui.
    {
      kind: 'group',
      id: 'cadastros',
      label: 'Cadastros',
      icon: <FolderPlus className="w-5 h-5" />,
      children: [
        {
          kind: 'leaf',
          id: 'cadastros-usuarios',
          label: 'Usuários',
          icon: <Users className="w-4 h-4" />,
          feature: FEATURES.usuarios,
          path: '/usuarios',
        },
        {
          kind: 'leaf',
          id: 'cadastros-empresas',
          label: 'Empresas',
          icon: <Building2 className="w-4 h-4" />,
          feature: FEATURES.empresas,
          path: '/empresas',
        },
        {
          kind: 'leaf',
          id: 'cadastros-fornecedores',
          label: 'Fornecedores',
          icon: <Truck className="w-4 h-4" />,
          feature: FEATURES.fornecedores,
          path: '/compras/fornecedores',
        },
        {
          kind: 'leaf',
          id: 'cadastros-clientes',
          label: 'Clientes',
          icon: <Briefcase className="w-4 h-4" />,
          feature: FEATURES.clientes,
          path: '/clientes',
        },
        // Cadastros organizacionais (change `pessoas-pre-requisitos`):
        // base da lotação, das movimentações e do cálculo de férias.
        {
          kind: 'leaf',
          id: 'cadastros-areas',
          label: 'Áreas',
          icon: <Network className="w-4 h-4" />,
          feature: FEATURES.masterDataAreas,
          path: '/cadastros/areas',
        },
        {
          kind: 'leaf',
          id: 'cadastros-cargos',
          label: 'Cargos',
          icon: <BadgeCheck className="w-4 h-4" />,
          feature: FEATURES.masterDataCargos,
          path: '/cadastros/cargos',
        },
        {
          kind: 'leaf',
          id: 'cadastros-feriados',
          label: 'Feriados',
          icon: <CalendarDays className="w-4 h-4" />,
          feature: FEATURES.masterDataFeriados,
          path: '/cadastros/feriados',
        },
        // Localidades dos ativos (issue #300): onde o equipamento está
        // alocado — unidade, cliente, estoque, assistência técnica.
        {
          kind: "leaf",
          id: "cadastros-localidades",
          label: "Localidades",
          icon: <MapPin className="w-4 h-4" />,
          feature: FEATURES.masterDataLocalidades,
          path: "/cadastros/localidades",
        },
        // Custodiários: colaboradores, prestadores, departamentos,
        // projetos e clientes — todos os "quem pode ser responsável
        // por ativo". Antes só dava pra criar ad-hoc pelo modal de
        // entrega; agora gestão dedicada com ativar/desativar (RN005).
        {
          kind: 'leaf',
          id: 'cadastros-custodiarios',
          label: 'Custodiários',
          icon: <UserCog className="w-4 h-4" />,
          feature: FEATURES.ativosCustodiarios,
          path: '/custodiarios',
        },
      ],
    },
    // Ativos fica em Compras como leaf único.
    // Dashboard / Lista / Inventário viram abas internas na AtivosHomePage.
    // Licenças (que é Ativo tipo Software) mescla na aba "Listar Ativos".
    // Alertas seguem no sino do header; Movimentações no histórico do detalhe.
    // Grupo "Comercial" — épico COM-001. Reúne pipeline de vendas.
    // Propostas, Contratos e OS entram como leaves nas specs seguintes.
    {
      kind: 'group',
      id: 'comercial',
      label: 'Comercial',
      icon: <Target className="w-5 h-5" />,
      children: [
        {
          kind: 'leaf',
          id: 'comercial-oportunidades',
          label: 'Oportunidades',
          icon: <TrendingUp className="w-4 h-4" />,
          feature: FEATURES.oportunidades,
          path: '/oportunidades',
        },
        {
          kind: 'leaf',
          id: 'comercial-contratos',
          label: 'Contratos',
          icon: <FileText className="w-4 h-4" />,
          feature: FEATURES.contratos,
          path: '/contratos',
        },
        {
          kind: 'leaf',
          id: 'comercial-bases',
          label: 'Bases Comerciais',
          icon: <FileText className="w-4 h-4" />,
          feature: FEATURES.baseComercial,
          path: '/base-comercial',
        },
      ],
    },
    // Grupo "Pessoas" — EPIC de Gestão de Pessoas (change
    // `pessoas-cadastro-mestre`). As próximas changes acrescentam leaves.
    {
      kind: 'group',
      id: 'pessoas',
      label: 'Pessoas',
      icon: <IdCard className="w-5 h-5" />,
      children: [
        {
          kind: 'leaf',
          id: 'pessoas-colaboradores',
          label: 'Colaboradores',
          icon: <Users className="w-4 h-4" />,
          feature: FEATURES.pessoasColaboradores,
          path: '/pessoas/colaboradores',
        },
        {
          kind: 'leaf',
          id: 'pessoas-validacoes',
          label: 'Validações pendentes',
          icon: <ClipboardCheck className="w-4 h-4" />,
          feature: FEATURES.pessoasDocumentosValidacao,
          anyFeatures: {
            features: [
              FEATURES.pessoasDocumentosValidacao,
              FEATURES.pessoasAlteracoesCadastrais,
              FEATURES.pessoasDadosBancariosValidacao,
            ],
            action: 'edit',
          },
          path: '/pessoas/validacoes',
        },
        {
          kind: 'leaf',
          id: 'pessoas-tipos-documentais',
          label: 'Tipos documentais',
          icon: <FileText className="w-4 h-4" />,
          feature: FEATURES.pessoasTiposDocumentais,
          path: '/pessoas/tipos-documentais',
        },
        {
          kind: 'leaf',
          id: 'pessoas-aprovacoes-movimentacao',
          label: 'Aprovações de movimentação',
          icon: <ListChecks className="w-4 h-4" />,
          feature: FEATURES.pessoasMovimentacoesAprovacao,
          anyFeatures: { features: [FEATURES.pessoasMovimentacoesAprovacao], action: 'edit' },
          path: '/pessoas/aprovacoes-movimentacao',
        },
        {
          kind: 'leaf',
          id: 'pessoas-motivos-situacao',
          label: 'Motivos de situação',
          icon: <ClipboardList className="w-4 h-4" />,
          feature: FEATURES.pessoasSituacaoFuncional,
          path: '/pessoas/motivos-situacao',
        },
        {
          kind: 'leaf',
          id: 'pessoas-aprovacoes-afastamento',
          label: 'Aprovações de afastamento',
          icon: <ListChecks className="w-4 h-4" />,
          feature: FEATURES.pessoasAfastamentosAprovacao,
          anyFeatures: { features: [FEATURES.pessoasAfastamentosAprovacao], action: 'edit' },
          path: '/pessoas/aprovacoes-afastamento',
        },
        {
          kind: 'leaf',
          id: 'pessoas-tipos-afastamento',
          label: 'Tipos de afastamento',
          icon: <ClipboardList className="w-4 h-4" />,
          feature: FEATURES.pessoasTiposAfastamento,
          path: '/pessoas/tipos-afastamento',
        },
        {
          kind: 'leaf',
          id: 'pessoas-tarefas-desligamento',
          label: 'Tarefas de desligamento',
          icon: <ListChecks className="w-4 h-4" />,
          feature: FEATURES.pessoasDesligamentoTarefas,
          anyFeatures: {
            features: [FEATURES.pessoasDesligamentoTarefas, FEATURES.pessoasDesligamentos],
          },
          path: '/pessoas/tarefas-desligamento',
        },
        {
          kind: 'leaf',
          id: 'pessoas-modelos-desligamento',
          label: 'Modelos de desligamento',
          icon: <ClipboardList className="w-4 h-4" />,
          feature: FEATURES.pessoasDesligamentoModelos,
          path: '/pessoas/modelos-desligamento',
        },
        {
          kind: 'leaf',
          id: 'pessoas-reintegracoes',
          label: 'Reintegrações pendentes',
          icon: <ListChecks className="w-4 h-4" />,
          feature: FEATURES.pessoasReintegracaoAprovacao,
          anyFeatures: {
            features: [FEATURES.pessoasReintegracaoAprovacao, FEATURES.pessoasReintegracaoJuridico],
            action: 'edit',
          },
          path: '/pessoas/reintegracoes',
        },
        {
          kind: 'leaf',
          id: 'pessoas-motivos-reintegracao',
          label: 'Motivos de reintegração',
          icon: <ClipboardList className="w-4 h-4" />,
          feature: FEATURES.pessoasReintegracoes,
          path: '/pessoas/motivos-reintegracao',
        },
        {
          kind: 'leaf',
          id: 'pessoas-aprovacoes-ferias',
          label: 'Aprovações de férias',
          icon: <ListChecks className="w-4 h-4" />,
          feature: FEATURES.pessoasFeriasAprovacao,
          anyFeatures: {
            features: [FEATURES.pessoasFeriasAprovacao, FEATURES.meusColaboradores],
            action: 'view',
          },
          path: '/pessoas/aprovacoes-ferias',
        },
        {
          kind: 'leaf',
          id: 'pessoas-calendario-ferias',
          label: 'Calendário de férias',
          icon: <CalendarDays className="w-4 h-4" />,
          feature: FEATURES.pessoasFerias,
          path: '/pessoas/calendario-ferias',
        },
        {
          kind: 'leaf',
          id: 'pessoas-acessos-ferias',
          label: 'Acessos nas férias',
          icon: <ShieldCheck className="w-4 h-4" />,
          feature: FEATURES.pessoasFeriasAcessos,
          path: '/pessoas/acessos-ferias',
        },
        {
          kind: 'leaf',
          id: 'pessoas-politicas-ferias',
          label: 'Políticas de férias',
          icon: <ClipboardList className="w-4 h-4" />,
          feature: FEATURES.pessoasFeriasPoliticas,
          path: '/pessoas/politicas-ferias',
        },
        {
          kind: 'leaf',
          id: 'pessoas-localidades',
          label: 'Endereços das localidades',
          icon: <Building2 className="w-4 h-4" />,
          feature: FEATURES.masterDataLocalidades,
          anyFeatures: { features: [FEATURES.masterDataLocalidades], action: 'edit' },
          path: '/pessoas/localidades',
        },
      ],
    },
    // Grupo "Recrutamento" — change `modulo-recrutamento-selecao`. Cada
    // leaf tem a feature da sua tela: quem só aprova pelo Financeiro vê
    // a fila de aprovações e nada mais.
    {
      kind: 'group',
      id: 'recrutamento',
      label: 'Recrutamento',
      icon: <UserPlus className="w-5 h-5" />,
      children: [
        {
          kind: 'leaf',
          id: 'recrutamento-requisicoes',
          label: 'Requisições de vaga',
          icon: <ClipboardList className="w-4 h-4" />,
          feature: FEATURES.recrutamentoRequisicoes,
          path: '/recrutamento/requisicoes',
        },
        {
          kind: 'leaf',
          id: 'recrutamento-aprovacoes',
          label: 'Aprovações pendentes',
          icon: <ListChecks className="w-4 h-4" />,
          feature: FEATURES.recrutamentoRequisicoes,
          // A fila é a mesma para os cinco passos da cadeia; o item
          // aparece para quem aprova por qualquer um deles.
          anyFeatures: {
            features: [
              FEATURES.recrutamentoAprovacaoGestor,
              FEATURES.recrutamentoAprovacaoRh,
              FEATURES.recrutamentoAprovacaoFinanceiro,
              FEATURES.recrutamentoAprovacaoDiretoria,
              FEATURES.recrutamentoAprovacaoRecrutamento,
            ],
            action: 'edit',
          },
          path: '/recrutamento/aprovacoes',
        },
        {
          kind: 'leaf',
          id: 'recrutamento-vagas',
          label: 'Vagas',
          icon: <KanbanSquare className="w-4 h-4" />,
          feature: FEATURES.recrutamentoPublicacao,
          path: '/recrutamento/vagas',
        },
        {
          kind: 'leaf',
          id: 'recrutamento-candidatos',
          label: 'Candidatos',
          icon: <Users2 className="w-4 h-4" />,
          feature: FEATURES.recrutamentoCandidatos,
          path: '/recrutamento/candidatos',
        },
        {
          kind: 'leaf',
          id: 'recrutamento-banco-talentos',
          label: 'Banco de talentos',
          icon: <Users2 className="w-4 h-4" />,
          feature: FEATURES.recrutamentoCandidatos,
          path: '/recrutamento/banco-talentos',
        },
      ],
    },
    {
      kind: 'leaf',
      id: 'admin-perfis',
      label: 'Perfis e Permissões',
      icon: <ShieldCheck className="w-5 h-5" />,
      feature: FEATURES.adminPerfis,
      path: '/admin/perfis',
    },
    {
      kind: 'leaf',
      id: 'portal-fornecedor',
      label: 'Meu Portal',
      icon: <Truck className="w-5 h-5" />,
      feature: FEATURES.portalFornecedor,
      path: '/portal/fornecedor',
    },
    {
      kind: 'leaf',
      id: 'rh-feedbacks',
      label: 'Revisão de Feedbacks',
      icon: <MessageSquare className="w-5 h-5" />,
      feature: FEATURES.feedbacksRh,
      path: '/rh/feedbacks',
    },
  ];

  const location = useLocation();

  function isLeafVisible(item: LeafItem) {
    const permitido = item.anyFeatures
      ? item.anyFeatures.features.some((feature) =>
          can(feature, item.anyFeatures?.action ?? 'view')
        )
      : can(item.feature);
    if (!permitido) return false;
    // A restrição por regime só se aplica a colaboradores; demais perfis não têm regime.
    // Regime desconhecido (sessão antiga em cache) mantém o comportamento histórico (PJ).
    if (item.employmentTypes && userRole === 'collaborator' && employmentType) {
      return item.employmentTypes.includes(employmentType);
    }
    return true;
  }

  function filterEntry(entry: MenuEntry): MenuEntry | null {
    if (entry.kind === 'leaf') {
      return isLeafVisible(entry) ? entry : null;
    }
    const visibleChildren = entry.children.filter(isLeafVisible);
    if (visibleChildren.length === 0) return null;
    return { ...entry, children: visibleChildren };
  }

  const visibleEntries = menuEntries.map(filterEntry).filter((e): e is MenuEntry => e !== null);

  // Grupos abertos por default se algum child está no path atual.
  const groupsOpenByDefault = new Set(
    visibleEntries
      .filter((e): e is GroupItem => e.kind === 'group')
      .filter((g) => g.children.some((c) => location.pathname.startsWith(c.path)))
      .map((g) => g.id)
  );
  const [openGroups, setOpenGroups] = useState<Set<string>>(groupsOpenByDefault);

  function toggleGroup(id: string) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function renderLeaf(item: LeafItem, indented = false) {
    const isActive =
      (item.id === 'cau-catalogo'
        ? location.pathname === '/cau' || location.pathname.startsWith('/cau/catalogo/')
        : item.id === 'cau-chamados'
          ? location.pathname === '/cau/chamados' || location.pathname.startsWith('/cau/chamados/')
          : item.id === 'compras-solicitacoes'
            ? location.pathname === '/compras' ||
              location.pathname.startsWith('/compras/solicitacoes/')
            : location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path + '/'))) ||
      (item.id === 'dashboard' && location.pathname === '/');
    return (
      <Link
        key={item.id + item.path}
        to={item.path}
        className={`w-full flex items-center space-x-3 rounded-lg transition-colors ${
          indented ? 'px-4 py-2 pl-11 text-sm' : 'px-4 py-3'
        } ${
          isActive
            ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-700'
            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
        }`}
        onClick={() => onNavigate(item.id)}
      >
        {item.icon}
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  }

  function renderGroup(group: GroupItem) {
    const isOpen = openGroups.has(group.id);
    const hasActiveChild = group.children.some(
      (c) => location.pathname === c.path || location.pathname.startsWith(c.path + '/')
    );
    return (
      <div key={group.id} className="space-y-1">
        <button
          type="button"
          onClick={() => toggleGroup(group.id)}
          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
            hasActiveChild
              ? 'bg-blue-50 text-blue-700'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          }`}
          aria-expanded={isOpen}
        >
          <span className="flex items-center space-x-3">
            {group.icon}
            <span className="font-medium">{group.label}</span>
          </span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>
        {isOpen && (
          <div className="space-y-1">{group.children.map((c) => renderLeaf(c, true))}</div>
        )}
      </div>
    );
  }

  return (
    <aside className="w-64 bg-gray-50 border-r border-gray-200 min-h-screen">
      <nav className="p-4 space-y-2">
        {visibleEntries.map((entry) =>
          entry.kind === 'leaf' ? renderLeaf(entry) : renderGroup(entry)
        )}
      </nav>
    </aside>
  );
};
